<template>
  <div class="login-card login-card--tenant">
    <div class="card-header">
      <img class="card-logo" :src="defaultLogo" alt="" />
      <h2 class="card-title">{{ t('loginPage.tenantLoginTitle') }}</h2>
      <p class="card-subtitle">{{ t('loginPage.tenantLoginSubtitle') }}</p>
    </div>

    <el-form
      ref="formRef"
      class="login-form"
      :rules="rules"
      label-position="top"
      :model="formLabelAlign"
      size="large"
      @submit.prevent
    >
      <el-form-item v-if="showTenantCode" :label="t('loginPage.tenantCode')" prop="tenantCode">
        <el-input
          v-model="formLabelAlign.tenantCode"
          :prefix-icon="OfficeBuilding"
          :placeholder="t('loginPage.tenantCodePh')"
          autocomplete="organization"
          @blur="onTenantCodeBlur"
        />
      </el-form-item>
      <el-form-item :label="t('field.username')" prop="username">
        <el-input
          v-model="formLabelAlign.username"
          :prefix-icon="User"
          :placeholder="t('loginPage.adminAccountPh')"
          autocomplete="username"
        />
      </el-form-item>
      <el-form-item :label="t('loginPage.passwordLabel')" prop="password">
        <el-input
          v-model="formLabelAlign.password"
          :prefix-icon="Lock"
          type="password"
          :placeholder="t('loginPage.passwordPh')"
          autocomplete="current-password"
          show-password
          @keyup.enter="subimt(formRef)"
        />
      </el-form-item>

      <el-button
        class="submit-btn"
        type="primary"
        :loading="state.loading"
        native-type="button"
        @click="subimt(formRef)"
      >
        {{ t('loginPage.loginBtn') }}
      </el-button>
    </el-form>
  </div>
</template>

<script setup>
import { reactive, ref } from 'vue'
import { User, Lock, OfficeBuilding } from '@element-plus/icons-vue'
import { useRouter } from 'vue-router'
import { loginApi } from '@/api/login.js'
import { ElMessage } from 'element-plus'
import defaultLogo from '@/assets/logo.png'
import { saveTenantBranding } from '@/utils/tenantBranding.js'
import { saveTenantSubscription } from '@/utils/tenantSubscription.js'
import { showTenantCode, defaultTenantCode } from '@/utils/deployConfig.js'
import {
  clearLayoutMenuState,
  isLoginResponsePlatformAdmin,
  savePortalSession,
  PORTAL_TENANT,
} from '@/utils/authSession.js'
import {
  getRememberedTenantCode,
  getTenantLoginRemember,
  saveTenantLoginRemember,
} from '@/utils/tenantLoginRemember.js'
import { usePageI18n } from '@/composables/usePageI18n.js'

const router = useRouter()
const formRef = ref()
const state = reactive({ loading: false })

const rules = reactive({
  ...(showTenantCode ? { tenantCode: [{ required: true, message: '', trigger: 'blur' }] } : {}),
  username: [{ required: true, message: '', trigger: 'blur' }],
  password: [{ required: true, message: '', trigger: 'blur' }],
})

const applyLabels = () => {
  if (showTenantCode && rules.tenantCode?.[0]) {
    rules.tenantCode[0].message = t('loginPage.tenantCodeRequired')
  }
  rules.username[0].message = t('loginPage.usernameRequired')
  rules.password[0].message = t('loginPage.passwordRequired')
}
const { t } = usePageI18n(applyLabels)
applyLabels()

const remembered = getTenantLoginRemember(
  showTenantCode ? getRememberedTenantCode() : (defaultTenantCode || getRememberedTenantCode())
)

const initialTenantCode = showTenantCode
  ? (remembered.tenantCode || '')
  : (defaultTenantCode || remembered.tenantCode || '')

const formLabelAlign = reactive({
  tenantCode: initialTenantCode,
  username: remembered.username,
  password: remembered.password,
})

const applyRememberedCredentials = (tenantCode) => {
  const saved = getTenantLoginRemember(tenantCode)
  formLabelAlign.username = saved.username
  formLabelAlign.password = saved.password
}

const onTenantCodeBlur = () => {
  applyRememberedCredentials(formLabelAlign.tenantCode)
}

const subimt = (formEl) => {
  state.loading = true
  if (!formEl) {
    state.loading = false
    return
  }
  formEl.validate((valid) => {
    if (!valid) {
      state.loading = false
      return
    }
    loginApi(JSON.stringify(formLabelAlign))
      .then((res) => {
        if (res.code == 200) {
          const code = (formLabelAlign.tenantCode || '').trim()
          saveTenantLoginRemember({
            tenantCode: code,
            username: formLabelAlign.username,
            password: formLabelAlign.password,
          })
          saveTenantBranding(res.data)
          saveTenantSubscription(res.data)
          const isPlatformAdmin = isLoginResponsePlatformAdmin(res.data)
          if (isPlatformAdmin) {
            ElMessage.error(t('loginPage.platformAdminDenied'))
            return
          }
          clearLayoutMenuState()
          savePortalSession(PORTAL_TENANT, {
            ...res.data,
            tenantCode: code,
          }, res.data.token)
          if (res.data.users.roleId === 2) {
            router.push('/captain-task')
          } else {
            router.push('/home')
          }
        } else {
          ElMessage.error(res.msg)
        }
      })
      .finally(() => {
        setTimeout(() => {
          state.loading = false
        }, 300)
      })
  })
}
</script>

<style lang="less" scoped>
.login-card--tenant {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 36px 40px 32px;
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.06);
}

.card-header {
  text-align: center;
  margin-bottom: 28px;
}

.card-logo {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  object-fit: cover;
  margin-bottom: 16px;
}

.card-title {
  margin: 0 0 6px;
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
}

.card-subtitle {
  margin: 0;
  font-size: 13px;
  color: #94a3b8;
  line-height: 1.5;
}

.login-form {
  :deep(.el-form-item) {
    margin-bottom: 18px;
  }

  :deep(.el-form-item__label) {
    padding-bottom: 4px;
    font-size: 13px;
    font-weight: 500;
    color: #475569;
  }

  :deep(.el-input__wrapper) {
    border-radius: 8px;
    box-shadow: 0 0 0 1px #e2e8f0 inset;
  }
}

.submit-btn {
  width: 100%;
  height: 42px;
  margin-top: 4px;
  font-size: 15px;
  font-weight: 600;
  border-radius: 8px;
}

@media (max-width: 480px) {
  .login-card--tenant {
    padding: 28px 24px 24px;
  }
}
</style>
